import type React from "react";
import { useState } from "react";

import { DotsLoader, NoSearchResultsFound } from "@similarweb/ui-components";

import useCompanyAutocompleteCustomFetch from "app/pages/sales-intelligence/modules/company-autocomplete/queries/useCompanyAutocompleteCustomFetch";
import type { IAutoCompleteSiteItem } from "app/@types/autocomplete";
import { translate } from "app/filters/i18nFilter";
import type { Competitor } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import CompanyAutocompleteSimpleItem from "app/pages/sales-intelligence/modules/company-autocomplete/components/CompanyAutocompleteSimpleItem/CompanyAutocompleteSimpleItem";

import { StyledAutocomplete, StyledNoSearchResultsFoundContainer } from "./styles";
import { mapAutocompleteOptionsToCompetitors, mapCompetitorsToAutocompleteOptions } from "./utils";

export const MAX_COMPETITORS = 4;

type CoompetitorsProps = {
    value: Competitor[];
    onAddCompetitor: (value: Competitor) => void;
    disabled?: boolean;
    defaultCompetitors?: Competitor[];
    placeholder?: string;
    searchIcon?: string;
};

export const CompetitorsAutocomplete: React.FC<CoompetitorsProps> = ({
    value,
    onAddCompetitor,
    disabled = false,
    defaultCompetitors,
    placeholder,
    searchIcon,
}) => {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const { getListItems, isLoading } = useCompanyAutocompleteCustomFetch();

    const filteredDefaultCompetitors = defaultCompetitors?.filter(({ domain }) => {
        return !value?.some((competitor) => competitor.domain === domain);
    }) || [];
    const defaultCompetitorsOptions = mapCompetitorsToAutocompleteOptions(filteredDefaultCompetitors);

    const handleAddCompetitor = (competitor: IAutoCompleteSiteItem) => {
        const competitorToAdd = mapAutocompleteOptionsToCompetitors(competitor);

        const isExist = value?.find(({ domain }) => domain === competitorToAdd.domain);
        if (isExist) {
            return;
        }
        onAddCompetitor(competitorToAdd);
    };

    const handleGetListItems = async (query: string) => {
        const term = query.trim();
        const cleanedTerm = term.toLowerCase().replace(/,/g, " ");

        if (term.length === 0) {
            return defaultCompetitorsOptions.map((company) => (
                <CompanyAutocompleteSimpleItem
                    key={company.name}
                    company={company}
                    queryWords={[]}
                    onClick={() => handleAddCompetitor(company)}
                />
            ));
        }
        const data = await getListItems(term);
        if (data.length === 0) {
            return (
                <StyledNoSearchResultsFoundContainer>
                    <NoSearchResultsFound searchTerm={term} />
                </StyledNoSearchResultsFoundContainer>
            );
        }

        return data.map((company) => (
            <CompanyAutocompleteSimpleItem
                key={company.name}
                company={company}
                queryWords={cleanedTerm.split(" ").filter(Boolean)}
                onClick={() => handleAddCompetitor(company)}
            />
        ));
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    return (
        <StyledAutocomplete
            searchIcon={searchIcon ? searchIcon : isFocused ? "search" : "plus"}
            placeholder={
                placeholder
                    ? placeholder
                    : isFocused
                      ? translate("si.pages.ai_agent.form.competitors.placeholder.search")
                      : translate("si.pages.ai_agent.form.competitors.placeholder")
            }
            loadingComponent={<DotsLoader />}
            floating={true}
            debounce={250}
            getListItems={handleGetListItems}
            isLoading={isLoading}
            onFocus={handleFocus}
            onBlur={handleBlur}
            isFocused={isFocused}
            disabled={disabled}
            resetValueOnSelect={true}
        />
    );
};
